import { Dispatch, MouseEventHandler, SetStateAction, useEffect, useRef, useState } from 'react';

import {
  Button,
  ButtonProps,
  Divider,
  Flex,
  HStack,
  IconButton,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { mdiForumOutline, mdiPhoneHangupOutline, mdiPhoneOutline } from '@mdi/js';
import { Icon } from '@mdi/react';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { BiCollapse } from 'react-icons/bi';
import { MdOutlinePhoneForwarded } from 'react-icons/md';
import { toast } from 'react-toastify';

import {
  AffiliateByPhoneNumberQuery,
  useAddParticipantMutation,
  useAffiliateByPhoneNumberLazyQuery,
  useHandleThunderOutboundCallMutation,
  useHangUpCallMutation,
} from '../../gql/generated/graphql';

import AffiliateModal from '../Calls/AffiliateModal';
import {
  THUNDER_MODAL_STORAGE_EVENT,
  THUNDER_MODAL_STORAGE_KEY,
  ThunderOutboundCallModalData,
  writeThunderModalStorage,
} from '../Calls/ThunderOutboundCallModalManager';

import SmallCurrentCallPanel from './SmallCurrentCallPanel';

import useDebounce from '../../hooks/useDebounce';
import { useCallsContext } from '../../libs/callsContext';
import { formatPhoneNumberForDisplay } from '../../libs/utils';

const CurrentCallPanel = () => {
  const { handleTransferCallModal, activeCall, current, setCurrent, answeredAt, refresh } =
    useCallsContext();
  const [panelMinimized, setPanelMinimized] = useState<boolean>(false);
  const [ringtone, setRingtone] = useState<HTMLAudioElement>();
  const [addParticipantMutation, { data: addParticipantMutationData }] =
    useAddParticipantMutation();
  const [handleThunderOutboundCall, { data: handleThunderOutboundCallData }] =
    useHandleThunderOutboundCallMutation();
  const panelRef = useRef<HTMLDivElement>(null);
  const [waitingMilliseconds, setWaitingMilliseconds] = useState(0);
  const [durationMilliseconds, setDurationMilliseconds] = useState(0);
  const [hangUpCallMutation] = useHangUpCallMutation();
  const [checkAffiliateByPhoneNumber] = useAffiliateByPhoneNumberLazyQuery();
  const pendingAffiliateDataRef = useRef<
    AffiliateByPhoneNumberQuery['affiliateByPhoneNumber'] | null
  >(null);
  const [isThunderModalOpen, setIsThunderModalOpen] = useState(() => {
    const stored = localStorage.getItem(THUNDER_MODAL_STORAGE_KEY);
    return stored === 'true';
  });
  // Disables the ANSWER button from click until the claim mutation settles, so a flip of
  // `current` to a second waiting call can't trigger a second claim (LE-16709).
  const [isAnswering, setIsAnswering] = useState(false);

  const leftButtonProps: ButtonProps = {
    variant: 'boot',
    size: 'lgWithIconLeft',
    leftIcon: <Icon path={mdiPhoneHangupOutline} size="20px" />,
  };

  const rightButtonProps: ButtonProps = {
    size: 'lgWithIconLeft',
  };

  const stopRingtone = () => {
    if (ringtone) {
      ringtone.pause();
      ringtone.currentTime = 0;
    }
  };

  // It shows hours only if needed. `utcToZonedTime` guarantees that the hours start from 0.
  const getTimeInterval = (milliseconds: number) =>
    format(utcToZonedTime(milliseconds, 'UTC'), milliseconds >= 3600000 ? 'HH:mm:ss' : 'mm:ss');

  const createInterval = (
    date: Date | string | null | undefined,
    setMilliseconds: Dispatch<SetStateAction<number>>,
  ) => {
    if (!date) {
      setMilliseconds(0);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    // Handle numeric string (timestamp in milliseconds) or date string/object
    let startTime: number;
    if (typeof date === 'string' && !Number.isNaN(Number(date))) {
      // String that represents a number (timestamp) - this is what GraphQL returns
      startTime = Number(date);
    } else if (date instanceof Date) {
      startTime = date.getTime();
    } else {
      // Try to parse as date string (ISO format)
      startTime = new Date(date).getTime();
    }

    if (Number.isNaN(startTime)) {
      setMilliseconds(0);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }
    const intervalId = setInterval(() => {
      const now = new Date().getTime();
      const timer = Math.floor(now - startTime);
      setMilliseconds(Math.max(0, timer));
    }, 1_000);

    // Calculate initial value immediately
    const now = new Date().getTime();
    const initialTimer = Math.floor(now - startTime);
    setMilliseconds(Math.max(0, initialTimer));

    return () => clearInterval(intervalId);
  };

  useEffect(() => {
    return createInterval(current?.started_at, setWaitingMilliseconds);
  }, [current?.started_at]);

  useEffect(() => {
    return createInterval(answeredAt, setDurationMilliseconds);
  }, [answeredAt]);

  useEffect(() => {
    const audio = new Audio('https://brass-hornet-8068.twil.io/assets/incoming_call_2.mp3');
    audio.volume = 0.2;
    setRingtone(audio);
  }, []);

  useEffect(() => {
    if (addParticipantMutationData?.addParticipant?.call_already_answered) {
      toast.error('This call was already answered by another rep');
      refresh();
    }
  }, [addParticipantMutationData, refresh]);

  useEffect(() => {
    if (handleThunderOutboundCallData?.handleThunderOutboundCall?.call_already_answered) {
      refresh();
    }
  }, [handleThunderOutboundCallData, refresh]);

  useEffect(() => {
    if (current && !activeCall && !isThunderModalOpen) {
      ringtone?.play();
    } else {
      stopRingtone();
    }
  }, [activeCall, ringtone, current, isThunderModalOpen]);

  const onClose = () => {
    setCurrent(undefined);
    handleTransferCallModal(false);
    stopRingtone();
    pendingAffiliateDataRef.current = null;
  };

  // Used to spread data across all open tabs
  const broadcastIgnoreCall = () => {
    localStorage.setItem('callIgnored', JSON.stringify('callIgnored'));
    localStorage.removeItem('callIgnored');
  };

  const debouncedAnswerCall = useDebounce(
    async () => {
      if (!current) {
        return;
      }
      if (isAnswering) {
        return;
      }
      setIsAnswering(true);
      try {
        // Thunder modal: thunderData.is_callback splits inbound (customer dialed back) vs
        // outbound (rep still needs to dial). Conversation history is fetched on demand
        // from the call bar's /thunder-conversation page, not carried in this payload.
        const { thunderData } = current;
        if (current.isThunder && thunderData) {
          const isCallback = !!thunderData.is_callback;
          const leadData: ThunderOutboundCallModalData = {
            phoneNumber: current.phoneNumber,
            fullname: current.fullname,
            scheduledTime: thunderData.scheduled_call_time,
            attemptNum: thunderData.attempt_num,
            thunderCallNotificationId: thunderData.thunder_call_notification_id,
            aiCallOpener: thunderData.ai_call_opener,
            thunderPhoneNumber: thunderData.thunder_phone_number,
            isCallback,
            lastAttemptTimestamp: thunderData.last_attempt_timestamp,
          };

          if (isCallback) {
            // Answer via addParticipantMutation BEFORE opening the modal — opening first
            // would trip the panel's `isThunderModalOpen && !activeCall` hide guard during
            // the SIP dial.
            try {
              const { data } = await addParticipantMutation({
                variables: {
                  hunt_group_slug: current.huntGroupSlug || '',
                  waiting_call_id: current.id ?? undefined,
                },
              });
              // The addParticipantMutationData effect above already toasts on "already
              // answered" — just broadcast + close here so we don't double-toast.
              if (data?.addParticipant?.call_already_answered) {
                broadcastIgnoreCall();
                onClose();
                return;
              }
            } catch (error) {
              toast.error('Failed to answer call');
              return;
            }

            writeThunderModalStorage(leadData);
            return;
          }
          // Outbound: claim the TCN and remove the fake waiting-call entry. No SIP dial yet —
          // the rep dials from inside the modal.
          try {
            const { data } = await handleThunderOutboundCall({
              variables: {
                hunt_group_slug: current.huntGroupSlug || '',
                thunder_call_notification_id: thunderData.thunder_call_notification_id ?? null,
                waiting_call_id: current.id ?? undefined,
              },
            });

            // Check if another rep already answered this call
            if (data?.handleThunderOutboundCall?.call_already_answered) {
              // Broadcast to other tabs first so they close immediately
              broadcastIgnoreCall();
              toast.error('This call was already answered by another rep');
              onClose();
              return;
            }

            writeThunderModalStorage(leadData);
          } catch (error) {
            toast.error('Failed to handle thunder outbound call');
          }
          return;
        }

        // Check for affiliate data BEFORE answering
        if (!current.dealId && current.phoneNumber) {
          try {
            const { data } = await checkAffiliateByPhoneNumber({
              variables: { phoneNumber: current.phoneNumber },
            });

            if (data?.affiliateByPhoneNumber) {
              // Store in ref - will show when activeCall is set (after call is answered)
              pendingAffiliateDataRef.current = data.affiliateByPhoneNumber;
            }
          } catch {
            // Silently fail
          }
        }

        // Answer the call
        try {
          await addParticipantMutation({
            variables: {
              hunt_group_slug: current.huntGroupSlug || '',
              waiting_call_id: current.id ?? undefined,
            },
          });
        } catch (error) {
          toast.error('Failed to answer call');
        }
      } finally {
        setIsAnswering(false);
      }
    },
    500,
    {
      leading: true,
      trailing: false,
      maxWait: 900,
    },
  );

  const receiveIgnoreCall = (ev: StorageEvent) => {
    if (ev.key === 'callIgnored') {
      onClose();
    }
  };

  // used for ringtone stoppage across multiple tabs, hides panel
  useEffect(() => {
    if (current) {
      window.addEventListener('storage', receiveIgnoreCall);
    } else {
      window.removeEventListener('storage', receiveIgnoreCall);
      handleTransferCallModal(false);
    }
  }, [current]);

  // Listen for ThunderOutboundCallModal state changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THUNDER_MODAL_STORAGE_KEY) {
        setIsThunderModalOpen(e.newValue === 'true');
      }
    };

    const handleCustomEvent = () => {
      const stored = localStorage.getItem(THUNDER_MODAL_STORAGE_KEY);
      setIsThunderModalOpen(stored === 'true');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(THUNDER_MODAL_STORAGE_EVENT, handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(THUNDER_MODAL_STORAGE_EVENT, handleCustomEvent);
    };
  }, []);

  const onIgnoreClick = () => {
    broadcastIgnoreCall();
    onClose();
  };

  const onHangUpClick = async () => {
    await hangUpCallMutation({
      variables: {
        conference_id: activeCall?.conferenceId ?? '',
      },
    });

    onClose();
  };

  const handlePanelClick: MouseEventHandler<HTMLDivElement> = (e) => {
    if (panelRef.current === e.target) {
      setPanelMinimized(true);
    }
  };

  // Only show modal when activeCall is set (call has been answered) AND we have pending affiliate data
  // This prevents flickering by waiting until after the transition is complete
  const shouldShowModal = activeCall && pendingAffiliateDataRef.current;
  const modalData = pendingAffiliateDataRef.current;

  // Hide panel if ThunderOutboundCallModal is open AND there's no active call (only hide for waiting calls)
  if (isThunderModalOpen && !activeCall) {
    return null;
  }

  // Render modal even when current is undefined (during transition)
  if (!current) {
    return shouldShowModal ? (
      <AffiliateModal
        isOpen
        onClose={() => {
          pendingAffiliateDataRef.current = null;
        }}
        affiliateData={modalData}
      />
    ) : null;
  }

  if (panelMinimized) {
    return (
      <>
        <SmallCurrentCallPanel onPanelClick={setPanelMinimized} phoneNumber={current.phoneNumber} />
        {shouldShowModal && (
          <AffiliateModal
            isOpen
            onClose={() => {
              pendingAffiliateDataRef.current = null;
            }}
            affiliateData={modalData}
          />
        )}
      </>
    );
  }

  const isRefi =
    current.dealType === 'refi' ||
    [
      '8339991584',
      '+18339991584',
      '8339991782',
      '+18339991782',
      '2086708843',
      '+12086708843',
      '+18335694156',
      '8335694156',
      '+18556070762',
      '8556070762',
      // fake mailer
      '+15205829264',
      '5205829264',
    ].includes(current.toNumber as string);

  // const isThunder = current.isThunder || false;
  const isThunder = false;

  const bgColor = isThunder ? 'thunderPurple' : isRefi ? 'refiCyan' : 'midnightblue';

  return (
    <Flex
      w="80%"
      maxW="1500px"
      h="80px" // If the height is changed, update `LEContainer`.
      position="fixed"
      bottom={0}
      right={0}
      left={0}
      mx="auto"
      bg={bgColor}
      alignItems="center"
      borderTopRadius="10px"
      zIndex={2000}
      justifyContent="space-between"
      fontWeight="bold"
      ref={panelRef}
      onClick={(event) => {
        handlePanelClick(event);
      }}
      color="white"
    >
      <HStack ml={4} h="full" gap={12}>
        {activeCall?.phoneNumber ? (
          <Button onClick={onHangUpClick} {...leftButtonProps}>
            END
          </Button>
        ) : (
          <Button onClick={onIgnoreClick} {...leftButtonProps}>
            IGNORE
          </Button>
        )}

        {activeCall?.phoneNumber ? (
          <HStack h="full" gap={2}>
            {activeCall?.fullname || activeCall?.dealId ? (
              <>
                <VStack spacing={0}>
                  {activeCall?.fullname ? <Text>{activeCall?.fullname}</Text> : null}
                  {activeCall?.dealId ? (
                    <Link
                      href={activeCall?.dealId ? `/deals/${activeCall?.dealId}` : undefined}
                      isExternal
                      fontSize="md"
                      style={{ textDecoration: 'underline' }}
                      mt={0}
                    >
                      {activeCall?.relationship ? (
                        <Text>
                          {activeCall?.relationship} - {activeCall?.dealId}
                        </Text>
                      ) : (
                        <Text>{activeCall?.dealId}</Text>
                      )}
                    </Link>
                  ) : null}
                </VStack>
                <Divider orientation="vertical" h="50%" />
              </>
            ) : null}
            <VStack spacing={0}>
              <Text>{formatPhoneNumberForDisplay(activeCall?.phoneNumber)}</Text>
              <Text textTransform="capitalize">{getTimeInterval(durationMilliseconds)}</Text>
            </VStack>
            {current?.isThunder && (
              <>
                <Divider orientation="vertical" h="50%" />
                <Link
                  href={`/thunder-conversation?phoneNumber=${activeCall?.phoneNumber}`}
                  target="_blank"
                >
                  <Icon path={mdiForumOutline} size="20px" />
                </Link>
              </>
            )}
          </HStack>
        ) : (
          <HStack h="full" gap={2}>
            <Text>{formatPhoneNumberForDisplay(current?.phoneNumber)}</Text>
            <Divider orientation="vertical" h="50%" />
            <Text textTransform="capitalize">Waiting {getTimeInterval(waitingMilliseconds)}</Text>
          </HStack>
        )}
      </HStack>

      <HStack mr={3}>
        {activeCall?.phoneNumber ? (
          <Button
            variant={isRefi ? 'textBorder' : isThunder ? 'secondary' : 'secondaryDarkBg'}
            leftIcon={<MdOutlinePhoneForwarded size={20} />}
            onClick={() => handleTransferCallModal(true)}
            {...rightButtonProps}
          >
            TRANSFER
          </Button>
        ) : (
          <Button
            leftIcon={<Icon path={mdiPhoneOutline} size="20px" />}
            onClick={debouncedAnswerCall}
            isLoading={isAnswering}
            isDisabled={isAnswering}
            {...rightButtonProps}
          >
            ANSWER
          </Button>
        )}

        <IconButton
          variant="icon"
          icon={<BiCollapse />}
          color="white"
          fontSize="3xl"
          aria-label="minimize panel"
          onClick={() => setPanelMinimized(true)}
        />
      </HStack>

      {shouldShowModal && (
        <AffiliateModal
          isOpen
          onClose={() => {
            pendingAffiliateDataRef.current = null;
          }}
          affiliateData={modalData}
        />
      )}
    </Flex>
  );
};

export default CurrentCallPanel;
